import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Terminal, ChevronDown } from 'lucide-react';
import { NAV_ITEMS } from '../../constants';
import { SOLUTION_DOMAINS } from '../../solutionData';

function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [solutionsOpen, setSolutionsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isSolutionsActive = location.pathname === '/solutions' || location.pathname.startsWith('/solutions/');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  return (
    <nav
      className={`fixed top-0 w-full z-50 transition-all duration-300 ${
        scrolled || isOpen
          ? 'bg-white/90 backdrop-blur-xl border-b border-slate-200/80 shadow-sm'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 sm:h-[4.25rem] md:h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 group min-h-[44px] min-w-[44px] items-center"
          >
            <div className="flex items-center justify-center w-9 h-9 rounded-lg bg-blue-600 text-white group-hover:bg-blue-700 transition-colors duration-300 shrink-0">
              <Terminal className="w-4 h-4 sm:w-5 sm:h-5" strokeWidth={2.25} />
            </div>
            <span className="text-sm sm:text-base font-semibold tracking-[0.2em] text-slate-900 uppercase">
              Infraverge
            </span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:block">
            <div className="flex items-center gap-1">
              {NAV_ITEMS.map((item) => {
                if (item.path === '/solutions') {
                  return (
                    <div
                      key={item.path}
                      className="relative"
                      onMouseEnter={() => setSolutionsOpen(true)}
                      onMouseLeave={() => setSolutionsOpen(false)}
                    >
                      <Link
                        to="/solutions"
                        className={`relative inline-flex items-center gap-0.5 px-4 py-2.5 min-h-[44px] items-center text-[13px] font-medium tracking-wide transition-colors duration-200 rounded-lg ${
                          isSolutionsActive
                            ? 'text-blue-600'
                            : 'text-slate-600 hover:text-slate-900'
                        }`}
                      >
                        {item.label}
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${solutionsOpen ? 'rotate-180' : ''}`} />
                        {isSolutionsActive && (
                          <span className="absolute bottom-1 left-4 right-4 h-px bg-blue-600 rounded-full" />
                        )}
                      </Link>
                      {solutionsOpen && (
                        <div className="absolute top-full left-0 pt-1">
                          <div className="rounded-lg border border-slate-200 bg-white py-2 shadow-lg min-w-[200px]">
                            {SOLUTION_DOMAINS.map((d) => (
                              <Link
                                key={d.id}
                                to={d.path}
                                className="block px-4 py-3 min-h-[44px] flex items-center text-[13px] font-medium text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                              >
                                {d.label}
                              </Link>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                }
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`relative inline-flex items-center min-h-[44px] px-4 py-2.5 text-[13px] font-medium tracking-wide transition-colors duration-200 rounded-lg ${
                      isActive
                        ? 'text-blue-600'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    {item.label}
                    {isActive && (
                      <span className="absolute bottom-1 left-4 right-4 h-px bg-blue-600 rounded-full" />
                    )}
                  </Link>
                );
              })}
            </div>
          </div>

          {/* CTA Button Desktop */}
          <div className="hidden md:block">
            <Link to="/contact">
              <span className="inline-block px-5 py-2.5 text-[13px] font-semibold tracking-wide text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-all duration-300 shadow-sm hover:shadow-md">
                Talk to us
              </span>
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsOpen(!isOpen)}
              aria-label={isOpen ? 'Close menu' : 'Open menu'}
              className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] p-3 rounded-lg text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors"
            >
              {isOpen ? <X size={22} strokeWidth={2} /> : <Menu size={22} strokeWidth={2} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div className="md:hidden bg-white border-t border-slate-200/80 shadow-xl">
          <div className="px-4 py-4 space-y-0.5">
            {NAV_ITEMS.map((item) => {
              if (item.path === '/solutions') {
                return (
                  <div key={item.path}>
                    <Link
                      to="/solutions"
                      className={`block px-4 py-3.5 rounded-lg text-[15px] font-medium min-h-[48px] flex items-center transition-colors ${
                        isSolutionsActive
                          ? 'text-blue-600 bg-blue-50'
                          : 'text-slate-600 hover:bg-slate-50 active:bg-slate-100'
                      }`}
                    >
                      {item.label}
                    </Link>
                    <div className="pl-6 pb-2 space-y-0.5">
                      {SOLUTION_DOMAINS.map((d) => (
                        <Link
                          key={d.id}
                          to={d.path}
                          className="block py-3 min-h-[44px] flex items-center text-[14px] text-slate-600 hover:text-slate-900"
                        >
                          {d.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              }
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block px-4 py-3.5 rounded-lg text-[15px] font-medium min-h-[48px] flex items-center transition-colors ${
                    location.pathname === item.path
                      ? 'text-blue-600 bg-blue-50'
                      : 'text-slate-600 hover:bg-slate-50 active:bg-slate-100'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
            <div className="pt-4 pb-2">
              <Link
                to="/contact"
                className="block w-full text-center text-white bg-blue-600 hover:bg-blue-700 px-4 py-3.5 rounded-lg font-semibold text-[15px] transition-colors"
              >
                Talk to us
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}

export default Navbar;
